import { CredentialDeviceType, PublicKeyCredentialCreationOptionsJSON, RegistrationResponseJSON } from "@simplewebauthn/server";
import { DLTInfo, DLTProtocolEnum, TransactionDecoded, TransactionSigner, WalletDLT } from "./blockchain";

export type AttributeValueType = string|number|boolean;
export interface WalletPublicInfo {
  chain: DLTProtocolEnum,
  address: string;
  publicKey?: string;
  dateCreation?: string;
  backupAddress: string;
  isMasterWallet?: boolean; // used to represent the master wallet of the instance for the chain
  nbConsecutiveFailure?: number; // count the number of password check failure happening in a row
  lastFailureTimestamp?: number; // millisec timestamp of the last recorded failure
  attributes: Record<string, AttributeValueType>;
}

interface WebAuthnAuthenticator {
  rpId?: string;
  credentialID: Uint8Array;
  credentialPublicKey: Uint8Array;
  counter: number;
  // Ex: 'singleDevice' | 'multiDevice'
  credentialDeviceType: CredentialDeviceType;
  credentialBackedUp: boolean;
  // Ex: ['ble' | 'hybrid' | 'internal' | 'nfc' | 'usb'] (kept as a plain string[] since
  // @simplewebauthn/server v14+ widened its own transport-related JSON types to string[])
  transports?: string[];
};

export interface WalletContent extends WalletPublicInfo{
  privateKey: string;
  passwordHash: string;
  webauthnChallenge?: string; // if set, the wallet is a webauthn wallet
  webauthn?: WebAuthnAuthenticator; // if set, the wallet is a webauthn wallet
}

export interface WalletCustodyRepo {
  save(wallet: WalletContent): Promise<WalletContent>;
  get(chain: DLTProtocolEnum, address: string): Promise<WalletContent|undefined>;
  getMasterWallets(chain: DLTProtocolEnum): Promise<WalletContent[]|undefined>;
  delete(chain: DLTProtocolEnum, address: string): Promise<WalletContent|undefined>;
  findByAttribute<T extends AttributeValueType>(chain: DLTProtocolEnum, attribute: string, value: T): Promise<WalletContent[]>;
}


export interface TokenPersistence {
  id: string;
  /** expiry date as in a jwt */
  exp: number;
  /** ony a single use? False by default */
  once?: boolean;
}
export interface TokenPersistenceRepo {
  save(token: TokenPersistence): Promise<TokenPersistence>;
  get(id: string): Promise<TokenPersistence|undefined>;
  delete(id: string): Promise<TokenPersistence|undefined>;
}

export interface WalletAuthenticationActivity {
  action: "sign" | "password-reset" | "delete" | "set-attributes" | "none";
  on?: {
    address?: string;
  },
}

export const passwordLessIndicator = "🔐generate-webauthn🔒";

export interface TokenPayload {
  iat: number;
  exp: number;
  issuer: string;
  activities: WalletAuthenticationActivity[];
  jti:string;
  persistent?: boolean
};

export interface WalletCustody {
  implement(chain: DLTProtocolEnum): boolean;

  newWallet(chain: DLTProtocolEnum, password: string, attributes: Record<string, AttributeValueType>, backupAddress?: string): Promise<string>;
  newWebAuthnWallet(chain: DLTProtocolEnum, attributes: Record<string, AttributeValueType>, backupAddress?: string): Promise<PublicKeyCredentialCreationOptionsJSON|undefined>;
  addWallet(chain: DLTProtocolEnum, password: string, privateKey: string, attributes: Record<string, AttributeValueType>, backupAddress?: string): Promise<string>;
  getWalletDLT(chain: DLTProtocolEnum, address: string, info: DLTInfo): Promise<WalletDLT|undefined>;
  registerWebauthn(chain: DLTProtocolEnum, address: string, authenticator: RegistrationResponseJSON): Promise<string|undefined>;
  /** return a temporary token that should be used to prove that the end-user knows the password */
  authenticate(chain: DLTProtocolEnum, address: string, password: string, activities:WalletAuthenticationActivity[]): Promise<string>;
  
  getSigner(chain: DLTProtocolEnum, address: string, token: string): Promise<TransactionSigner>;
  
  deleteWallet(chain: DLTProtocolEnum, address: string, token: string): Promise<string>;
  
  updatePassword(chain: DLTProtocolEnum, address: string, newPassword: string, token: string): Promise<string>;
  updateAttributes(chain: DLTProtocolEnum, address: string, attributes: Record<string, AttributeValueType|undefined>, token: string): Promise<WalletPublicInfo>;
  
  publicInfo(chain: DLTProtocolEnum, address: string): Promise<WalletPublicInfo|undefined>;
  findWallets<T extends AttributeValueType>(chain: DLTProtocolEnum, attribute: string, value: T): Promise<WalletPublicInfo[]>;
}

export interface WalletCustodyApi {
  get protocol(): DLTProtocolEnum;
  newWallet(password: string, backupAddress?: string): Promise<string>;
  newWalletWithAttributes(password: string, attributes: Record<string, AttributeValueType>, backupAddress?: string): Promise<string>;
  newWebauthnWalletWithAttributes(attributes: Record<string, AttributeValueType>, backupAddress?: string): Promise<PublicKeyCredentialCreationOptionsJSON>;
  publicInfo(address: string): Promise<WalletPublicInfo & {exists: boolean}>;
  findByAttribute<T extends AttributeValueType>(attribute: string, value: T): Promise<WalletPublicInfo[]>;
  authenticate(address: string, password: string, activities?:WalletAuthenticationActivity[]): Promise<string>;
  sign(address: string, token: string, rawTx: string, dltInfo: DLTInfo): Promise<string>;
  delete(address: string, token: string): Promise<string>;
  updatePassword(address: string, newPassword: string, token: string): Promise<string>;
  updateAttributes(address: string, updated: Record<string, AttributeValueType>, deleted: string[], token: string): Promise<WalletPublicInfo>;
  getSigner(address: string, password: string): TransactionSigner;
}

export interface TxMonitorServices {
  decodeSavedTransactions(): Promise<number>;
  searchTransactions(filter:any): Promise<TxMonitorRecord[]>;
}

export interface TxMonitorRecord {
  /** Storage id */
  uid: string;
  chain: DLTProtocolEnum;
  address: string;
  timestamp: number;
  data: Buffer;
  signed: boolean;
  failedRule?: string;
  decoded?: TransactionDecoded;
}
export interface TxMonitorRepository {
  save(timestamp: number, from: WalletDLT, tx: Buffer, decoded: TransactionDecoded|undefined, signed: boolean, failedRule?: string): Promise<TxMonitorRecord|undefined>;
  getTxNotDecoded(chain: DLTProtocolEnum): Promise<TxMonitorRecord[]>;
  update(record: Partial<TxMonitorRecord>): Promise<TxMonitorRecord>;
  search(filter:any): Promise<TxMonitorRecord[]>;
}

export interface TxMonitorRuleResult {
  id: string;
  result: boolean;
}; 
// export interface TxMonitorRule {
//   id: string;
//   impl: (from:WalletDLT, tx:Buffer, decoded?: TransactionDecoded)=>Promise<TxMonitorRuleResult>;
// }
export interface TxMonitorRulesEngine {
  // list(from: WalletDLT, tx: Buffer, decoded?: TransactionDecoded): Promise<TxMonitorRule[]>;
  check(from: WalletDLT, tx: Buffer, timestamp:number, decoded?: TransactionDecoded): Promise<TxMonitorRuleResult[]>;
}

/** Generic storage interface to save and get the ABI elements */
export interface ContractDefinitionsRepository<T = any> {
  save(key: string, definition: T): Promise<T>;
  get(key: string): Promise<T|undefined>;
  all(): Promise<T[]>;
}
